import { ReactiveComponent } from "../../../core/ReactiveComponent.core";

import { Vector2, Vector3, Plane } from 'three';
import { CSS3DRenderer, CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { indicator2D } from "../../components";
import { ICON } from "../../Icons/Icons";
import gsap from "gsap";

export class Postit extends ReactiveComponent {
  constructor(params) {
    super(null, {
      mode: 'view', // 'view' | 'edit'
      title: (params?.data?.title) || 'Title',
      text: (params?.data?.text) || 'Write your note here'
    });

    const { camera, controls, cssRenderer, raycaster, position, id, creator, text } = params || {};
    this.id = id;
    this.camera = camera;
    this.position = position;
    this.controls = controls || null;
    this.cssRenderer = cssRenderer;
    this.model = params.model;
    this._raycaster = raycaster;
    this._ndc = new Vector2();
    this._dragPlane = new Plane();
    this._planeIntersectPoint = new Vector3();
    this._tmpVec = new Vector3();
    this._maxDepth = 3000;
    this.userId = creator;
    this.state.text = text;
    this.canDelete = this.model.config.isOwner || this.model.config.isAdmin || (this.model.config.userId === creator) || this.model.config.isContributor;
    this._ns = `.postit-${id}`;
    this.postitTitleELementFromModal = $('#postitModal .postit-title');
    this.postitTitleImageElementFromModal = $('#postitModal .postit-title-image-wrapper img');
    this.postitModal = $('#postitModal');
    this.postitBodyElementFromModal = $('#postitModal .postit-content');
    this.postitFooter = $('#postitModal footer');
    this.postitPrimaryButton = $('#postitModal .buttn.primary');
    this.postitSecondaryButton = $('#postitModal .buttn.secondary');

    //close
    this.closeCallbackFromModal = (e) => {
      if (this.postitModal.attr('data-postit-id') !== String(this.id)) return;
      e.preventDefault();
      e.stopPropagation();
      this.postitModal.removeClass('is-active');
      this.smallSize();
    };
    this.closeButtonFromModalBottom = $('#postitModal .buttn.secondary');
    this.closeButtonFromModalBottom.off(`click${this._ns}`).on(`click${this._ns}`, (e) => { this.closeCallbackFromModal(e); });
    this.closeButtonFromModal = $('#postitModal .close-modal');
    this.closeButtonFromModal.off(`click${this._ns}`).on(`click${this._ns}`, (e) => { this.closeCallbackFromModal(e); });


    // Ensure a delete button exists in modal footer (only for allowed users)
    if (this.canDelete && !this.postitFooter.find('.postit-delete').length) {
      const deleteBtnEl = $(`<button class="buttn danger postit-delete" data-lang="delete-text">Delete</button>`);
      this.postitFooter.prepend(deleteBtnEl);
    }

    // Primary button toggles between Edit and Save in the modal
    this.editButtonFromModal = $('#postitModal .buttn.primary');
    this.editButtonFromModal.off(`click${this._ns}`).on(`click${this._ns}`, (e) => {
      if (this.postitModal.attr('data-postit-id') !== String(this.id)) return;
      e.preventDefault();
      e.stopPropagation();
      const currentMode = this.postitModal.attr('data-mode') || 'view';
      if (currentMode === 'create') return;
      if (currentMode === 'view') {
        this._modalSetMode('edit');
        return;
      }
      // Save flow in modal
      const textarea = $('#postitModal .postit-textarea');
      const newText = textarea.val() || '';
      const payload = {
        position: {
          x: this.object.position.x,
          y: this.object.position.y,
          z: this.object.position.z,
        },
        text: newText,
        scene_id: this.model.currentScene?.id,
      };
      this.model.postitFetcher.updatePostit(this.id, payload, () => {
        this.state.text = newText;
        if (this._bodyView) this._bodyView.textContent = this.state.text;
        this._modalSetMode('view');
        this.model.info && this.model.info('info', $trans('postit-updated') || 'Updated');
      }, () => {
        this.model.info('error', $trans('only-creator-can-update-postit'));
      });
    });
    // Delete from modal (guard by postit id on the modal)
    this.postitFooter.off(`click${this._ns}`, '.postit-delete').on(`click${this._ns}`, '.postit-delete', (e) => {
      if (this.postitModal.attr('data-postit-id') !== String(this.id)) return;
      e.preventDefault();
      e.stopPropagation();
      this.model.confirm($trans('confirm-delete-postit'), () => {
        this.model.postitFetcher.deletePostit(this.id, () => {
          this.model.info('info', $trans('postit-deleted'));
          this.postitModal.removeClass('is-active');
          this.model.reloadPostits();
        }, { dimmerClose: false }, () => {
          console.log('error deleting postit');
        });
      });
    });

    this.isDragging = false;

    this._buildNode();

    this.object.position.set(this.position.x, this.position.y, (this.position?.z !== undefined ? this.position.z : -600));
    // initial mode render
    this._updateMode('view');
    this.smallSize();

    const callback = (response) => {
      const { data: { avatar_image_url, username } } = response;
      this?._avatarImg?.setAttribute('src', avatar_image_url);
      this.smallImageOverlay.setAttribute('src', avatar_image_url);
      this.state.avatar_image_url = avatar_image_url;
      // Ensure state reflects the displayed title so toggling modes doesn't reset it
      this.state.title = username || this.state.title;
      const titleEl = this?._titleTextEl;
      if (titleEl) titleEl.textContent = this.state.title;
    }
    params.model.worldFetcher.getProfileInfo(creator, callback);
    return this;
  }

  _buildNode() {
    const root = document.createElement('div');
    root.className = 'postit';
    root.setAttribute('data-css3d-interactive', 'true');
    root.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.isSmallSize && this.largeSize();
      if (this._bodyInput && this.state.mode === 'edit') this._bodyInput.focus();
    });
    this.root = root;


    const titleView = document.createElement('div');
    titleView.className = 'postit__title';
    this._titleView = titleView;

    // Creator avatar on the left of the title
    const avatarWrap = document.createElement('div');
    avatarWrap.className = 'postit__avatar';

    // Avatar image inside wrapper
    const avatarImg = document.createElement('img');
    avatarImg.alt = 'avatar';
    avatarWrap.appendChild(avatarImg);

    avatarWrap.addEventListener('pointerdown', (e) => {
      this.model.showUserInfo({
        data: null,
        userId: 75,
      });
    });
    const titleText = document.createElement('span');
    titleText.textContent = this.state.title;
    titleText.className = 'postit__title-text';

    titleView.appendChild(avatarWrap);
    titleView.appendChild(titleText);

    // Title is fixed text; no input field even in edit mode

    const bodyView = document.createElement('div');
    bodyView.className = 'postit__body';
    bodyView.textContent = this.state.text;
    this._bodyView = bodyView;

    const bodyInput = document.createElement('textarea');
    bodyInput.placeholder = 'Write your note here';
    bodyInput.className = 'postit__input';
    bodyInput.value = this.state.text;

    // Drag handle (visible only in edit mode)
    const handle = document.createElement('div');
    handle.className = 'postit__handle';

    // Add expand icon to handle
    try {
      const handleIcon = $(ICON.EXPAND)[0];
      if (handleIcon) handle.appendChild(handleIcon);
    } catch (e) { /* no-op */ }

    let toggle = null;
    let deleteBtn = null;
    if (!this.model.config.isGuest) {
      toggle = document.createElement('button');
      toggle.textContent = 'Edit';
      toggle.className = 'postit__toggle';
      // Use pointerdown to avoid parent listeners (e.g., OrbitControls) suppressing click
      toggle.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Open modal directly in edit mode
        this.largeSize();
        this._modalSetMode('edit');
      });

      // Bottom-left delete button styled like Edit but transparent red
      if (this.canDelete) {
        deleteBtn = document.createElement('button');
        deleteBtn.textContent = 'Delete';
        deleteBtn.className = 'postit__delete';
        deleteBtn.addEventListener('pointerup', (e) => {
          e.preventDefault();
          e.stopPropagation();
          ///delete postit
          this.model.confirm($trans('confirm-delete-postit'), () => {
            this.model.postitFetcher.deletePostit(this.id, () => {
              console.log('postit deleted');
              this.model.info('info', $trans('postit-deleted'));
              this.model.reloadPostits();
            }, { dimmerClose: false }, () => {
              console.log('error deleting postit');
            });
          });
        });
        this.deleteBtn = deleteBtn;
      }
    }

    const indicator2DElement = $(indicator2D(1, '', 'note', 'Note', 'note'));
    indicator2DElement[0].style.zIndex = '1010';

    ///add close "x" top right on postit element
    const closeButton = document.createElement('div');
    const closeButtonElement = $(`<span class="material-icons md-26 clickable confirm-close">close</span>`)[0];
    closeButtonElement.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._updateMode('view');
      this.smallSize();
    });
    closeButton.append(closeButtonElement);
    closeButton.className = 'postit__close';
    this.closeButton = closeButton;
    closeButton.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._updateMode('view');
      this.smallSize();
    });

    const smallImageOverlayWrapper = document.createElement('div');
    const smallImage = document.createElement('img');
    smallImage.alt = 'small image';
    smallImage.src = '/textures/bagless_logo.png';
    smallImageOverlayWrapper.className = 'postit__small-image-overlay-wrapper';
    smallImageOverlayWrapper.appendChild(smallImage);
    this.smallImageOverlay = smallImage;
    this.smallImageOverlayWrapper = smallImageOverlayWrapper;

    root.appendChild(handle);
    root.appendChild(titleView);
    root.appendChild(bodyView);
    root.appendChild(indicator2DElement[0]);
    root.appendChild(closeButton);
    root.appendChild(smallImageOverlayWrapper);

    if (toggle) root.appendChild(toggle);
    if (deleteBtn) root.appendChild(deleteBtn);

    this.dom = root;
    this.object = new CSS3DObject(root);
    // refs
    this._titleView = titleView;
    this._titleTextEl = titleText;
    this._bodyView = bodyView;
    this._avatarImg = avatarImg;
    // no title input; title remains fixed text
    this._bodyInput = bodyInput;
    this._toggle = toggle;
    this._dragHandle = handle;
  }


  _clampToMaxDepth(target) {
    const forward = new Vector3();
    this.camera.getWorldDirection(forward);
    this._tmpVec.subVectors(target, this.camera.position);
    const depth = this._tmpVec.dot(forward);
    if (depth > this._maxDepth) {
      target.copy(this.camera.position).add(forward.multiplyScalar(this._maxDepth));
    }
    return target;
  }

  _setFromClientXY(clientX, clientY) {
    this._ndc.x = (clientX / window.innerWidth) * 2 - 1;
    this._ndc.y = -(clientY / window.innerHeight) * 2 + 1;
    this._raycaster.setFromCamera(this._ndc, this.camera);
    if (this._raycaster.ray.intersectPlane(this._dragPlane, this._planeIntersectPoint)) {
      this._clampToMaxDepth(this._planeIntersectPoint);
      this.object.position.copy(this._planeIntersectPoint);
    }
  }

  _onPointerMove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!this.isDragging) return;
    this._setFromClientXY(e.clientX, e.clientY);
  };

  _onPointerUp = (e) => {
    if (!this.isDragging) return;
    this.isDragging = false;
    if (this._dragHandle) this._dragHandle.style.cursor = 'grab';
    if (this._captureEl) {
      try { this._captureEl.releasePointerCapture(e.pointerId); } catch (err) { }
      this._captureEl = null;
    }
    window.removeEventListener('pointermove', this._onPointerMove);
    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerUp);
    if (this.controls) this.controls.enabled = true;
  };

  _onPointerDown = (e) => {
    if (this.state.mode !== 'edit') return;
    // If listener is on handle, inputs/buttons won't be targeted, keep guard anyway
    if (e.target && (e.target.closest('input') || e.target.closest('textarea') || e.target.closest('button'))) return;
    this.isDragging = true;
    this._captureEl = e.currentTarget || this.dom;
    try { this._captureEl.setPointerCapture(e.pointerId); } catch (err) { }
    const cameraNormal = new Vector3();
    this.camera.getWorldDirection(cameraNormal);
    this._dragPlane.setFromNormalAndCoplanarPoint(cameraNormal, this.object.position);
    if (this._dragHandle) this._dragHandle.style.cursor = 'grabbing';
    window.addEventListener('pointermove', this._onPointerMove);
    window.addEventListener('pointerup', this._onPointerUp);
    window.addEventListener('pointercancel', this._onPointerUp);
    if (this.controls) this.controls.enabled = false;
  };

  _attachDragListeners() {
    if (this._dragHandle) {
      this._dragHandle.addEventListener('pointerdown', this._onPointerDown);
      this._dragHandle.style.cursor = 'grab';
      this._dragHandle.style.display = 'flex';
    }
  }

  _detachDragListeners() {
    if (this._dragHandle) {
      this._dragHandle.removeEventListener('pointerdown', this._onPointerDown);
      this._dragHandle.style.display = 'none';
    }
    window.removeEventListener('pointermove', this._onPointerMove);
    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerUp);
    this.isDragging = false;
    if (this._dragHandle) this._dragHandle.style.cursor = 'grab';
    if (this.controls) this.controls.enabled = true;
  }

  _updateMode(mode, save = false) {
    this.state.mode = mode;
    if (this.state.mode === 'edit') {
      this._attachDragListeners();
      this._bodyInput.value = this.state.text || '';
      if (this._bodyView && this._bodyView.parentNode) this._bodyView.parentNode.replaceChild(this._bodyInput, this._bodyView);
      // focus body input for quick editing
      setTimeout(() => { try { this._bodyInput && this._bodyInput.focus(); } catch (err) { } }, 0);
      // show handle instead of whole note being draggable
      ///add "view" to button toggle
      this._toggle && (this._toggle.textContent = 'Save');
      this.dom.style.cursor = 'default';
      this.isSmallSize && this.largeSize();
      this.deleteBtn && (this.deleteBtn.style.display = 'block');
      /// update postit
    }
    else if (this.state.mode === 'view') {
      // Scale the 3D object instead of the DOM to avoid mirrored/inverted content
      gsap.to(this.object.scale, { duration: 0.35, ease: 'expo.out', x: 1, y: 1, z: 1 });
      this._detachDragListeners();
      // title stays fixed; do not read from input
      this.state.text = this._bodyInput.value || '';
      if (this._titleTextEl) this._titleTextEl.textContent = this.state.title;
      if (this._bodyView) this._bodyView.textContent = this.state.text;
      if (this._bodyInput && this._bodyInput.parentNode) this._bodyInput.parentNode.replaceChild(this._bodyView, this._bodyInput);
      // in view mode, no drag handle
      this.dom.style.cursor = 'default';
      ///add "edit" to button toggle
      this._toggle && (this._toggle.textContent = 'Edit');
      this.deleteBtn && (this.deleteBtn.style.display = 'none');
      if (save) {
        this.model.postitFetcher.updatePostit(this.id, {
          position: {
            x: this.object.position.x,
            y: this.object.position.y,
            z: this.object.position.z,
          },
          text: this._bodyInput.value,
          scene_id: this.model.currentScene?.id,
        }, () => {
          console.log('postit updated');
        }, () => {
          this.model.info('error', $trans('only-creator-can-update-postit'));
        });
      }
      this.smallSize();
    }
  }

  _modalSetMode(mode) {
    // Store mode on modal for this postit
    this.postitModal.attr('data-mode', mode);
    this.postitModal.removeClass('is-create-mode');
    this.postitSecondaryButton && this.postitSecondaryButton.show();
    if (mode === 'edit') {
      // Switch content to textarea for editing
      const textarea = $(`<textarea class="postit-textarea" placeholder="Write your note here"></textarea>`);
      textarea.val(this.state.text || '');
      this.postitBodyElementFromModal.empty().append(textarea);
      // Primary button shows Save
      this.postitPrimaryButton.text($trans('save-text', {}, 'Save')).show();
      // Show delete button if allowed
      if (this.canDelete) {
        this.postitFooter.find('.postit-delete').show();
      }
    } else {
      // View mode: show plain content
      this.postitBodyElementFromModal.text(this.state.text || '');
      // Primary button shows Edit (only if allowed to edit)
      this.postitPrimaryButton.text($trans('edit-text', {}, 'Edit'));
      if (this.canDelete) {
        this.postitPrimaryButton.show();
        this.postitFooter.find('.postit-delete').hide();
      } else {
        this.postitPrimaryButton.hide();
      }
    }
  }

  render(camera) {
    if (!camera) return;
    this.object.lookAt(camera.position);
    this._clampToMaxDepth(this.object.position);
    ///sprite3d
    //   if (!camera) return;
    //   this.object.quaternion.copy(camera.quaternion);
    //   this._clampToMaxDepth(this.object.position);
  }

  smallSize() {
    this.isSmallSize = true;
    gsap.to(this.object.scale, { duration: 0.35, ease: 'expo.out', x: 0.25, y: 0.25, z: 0.25 });
    this.closeButton.style.display = 'none';
    this._titleView.style.display = 'none';
    this.smallImageOverlayWrapper.style.display = 'block';
    this._avatarImg.style.display = 'none';
    this._bodyView.style.display = 'none';
    this._toggle && (this._toggle.style.display = 'none');
    this.postitModal.removeClass('is-active');

  }
  largeSize() {
    // Open the modal and populate with current state
    this.postitModal.removeClass('is-create-mode');
    this.postitModal.removeAttr('data-saving');
    this.postitTitleELementFromModal.html(this.state.title);
    if (this.state.avatar_image_url) {
      this.postitTitleImageElementFromModal.attr('src', this.state.avatar_image_url);
    }
    this._modalSetMode('view');
    this.postitModal.attr('data-postit-id', this.id);
    this.postitModal.addClass('is-active');
  }

  static openCreateDialog({ model, camera } = {}) {
    if (!model || !camera) return;

    const modal = $('#postitModal');
    const titleEl = modal.find('.postit-title');
    const bodyEl = modal.find('.postit-content');
    const footer = modal.find('footer');
    let primary = modal.find('.buttn.primary');
    const secondary = modal.find('.buttn.secondary');
    const closeModalBtn = modal.find('.close-modal');

    if (!primary.length) {
      primary = $(`<button class="buttn primary" data-lang="save-text">${$trans('save-text', {}, 'Save')}</button>`);
      footer.prepend(primary);
    }

    modal.addClass('is-create-mode');
    modal.attr('data-mode', 'create');
    modal.attr('data-postit-id', 'create');
    modal.removeAttr('data-saving');
    titleEl.text($trans('add-postit', {}, 'Add post it'));

    const textarea = $(`<textarea class="postit-textarea" placeholder="Write your note here"></textarea>`);
    bodyEl.empty().append(textarea);

    primary.text($trans('save-text', {}, 'Save')).show().removeClass('disabled');
    secondary.hide();
    footer.find('.postit-delete').hide();

    const avatarUrl = model.config && model.config.userAvatarUrl;
    if (avatarUrl) {
      modal.find('.postit-title-image-wrapper img').attr('src', avatarUrl);
    }

    const closeCreate = (e) => {
      if (modal.attr('data-postit-id') !== 'create') return;
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      modal.removeClass('is-active is-create-mode');
      modal.removeAttr('data-saving');
      secondary.show();
    };

    closeModalBtn.off('click.postit-create').on('click.postit-create', closeCreate);

    primary.off('click.postit-create').on('click.postit-create', (e) => {
      if (modal.attr('data-postit-id') !== 'create') return;
      e.preventDefault();
      e.stopPropagation();
      if (modal.attr('data-saving') === '1') return;

      const text = textarea.val() || '';
      const pos = camera.position.clone();
      const forward = new Vector3();
      camera.getWorldDirection(forward);
      pos.add(forward.multiplyScalar(700));

      modal.attr('data-saving', '1');
      primary.addClass('disabled');

      model.postitFetcher.createPostit(model.config.worldId, {
        position: pos,
        text,
        scene_id: model.currentScene && model.currentScene.id,
      }, () => {
        modal.removeAttr('data-saving');
        primary.removeClass('disabled');
        model.info && model.info('info', $trans('postit-created'));
        closeCreate();
        model.reloadPostits();
      }, (error) => {
        modal.removeAttr('data-saving');
        primary.removeClass('disabled');
        if (window.openLoginModal) {
          window.openLoginModal($trans('you-need-to-login-to-create-postit'));
        }
        model.info && model.info('info', $trans('not-allowed-to-create-postit'));
        model.error && model.error('error', $trans('error-creating-postit'), error);
      });
    });

    modal.addClass('is-active');
    setTimeout(() => { try { textarea.trigger('focus'); } catch (err) { /* no-op */ } }, 0);
  }
  updatePostit() {
    const data = {
      position: {
        x: this.object.position.x,
        y: this.object.position.y,
        z: this.object.position.z,
      },
      text: this._bodyInput.value,
    };
    this.model.postitFetcher.updatePostit(this.id, data, () => {
      console.log('postit updated');
    }, () => {
      console.log('error updating postit');
    });
  }

  destroy() {
    try {
      this.editButtonFromModal && this.editButtonFromModal.off(`click${this._ns}`);
      this.closeButtonFromModalBottom && this.closeButtonFromModalBottom.off(`click${this._ns}`);
      this.closeButtonFromModal && this.closeButtonFromModal.off(`click${this._ns}`);
      this.postitFooter && this.postitFooter.off(`click${this._ns}`, '.postit-delete');
      this._detachDragListeners();
    } catch (e) { /* no-op */ }
  }
}